import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { usePageMeta, organizationSchema, faqSchema, breadcrumbSchema } from '../lib/seo';
import { guides } from '../data/siteData';
import {
  AnswerBlock,
  ButtonLink,
  Card,
  Container,
  CTASection,
  FAQList,
  PageHero,
  Prose,
  Section,
  SectionHeading,
} from '../components/SiteUi';

export function GuidesHubPage() {
  usePageMeta({
    title: 'AI Receptionist Guides for Australian Businesses – CloudNext',
    description: 'Plain-English guides to AI receptionists, pricing, booking, what happens when it can’t answer, and phone numbers for Australian small businesses.',
    path: '/guides',
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Guides', path: '/guides' }])],
  });

  return (
    <>
      <PageHero
        eyebrow="Guides"
        title={<>Understand the <span className="text-gold-gradient">AI receptionist</span> before you buy.</>}
        description="No jargon. What it does, what it costs, how booking works and what happens when it cannot answer."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Guides' }]}
      />
      <Section>
        <Container>
          <div className="grid gap-5 md:grid-cols-2">
            {guides.map((guide) => (
              <Link key={guide.slug} to={`/guides/${guide.slug}`} className="group rounded-3xl border border-white/[0.06] bg-white/[0.02] p-6 transition hover:-translate-y-0.5 hover:border-gold/40">
                <h2 className="font-display text-xl text-cream group-hover:text-gold-light">{guide.title}</h2>
                <p className="mt-3 text-sm leading-6 text-dust">{guide.description}</p>
                <span className="mt-5 inline-block text-sm text-gold-light">Read the guide →</span>
              </Link>
            ))}
          </div>
        </Container>
      </Section>
      <CTASection title="Want to see it in action?" description="Book a call and ask the questions that matter to your business." primaryTo="/contact" primaryLabel="Book a call" secondaryTo="/ai-receptionist" secondaryLabel="See the AI receptionist" />
    </>
  );
}

export function GuidePage() {
  const { slug } = useParams();
  const guide = guides.find((item) => item.slug === slug);
  const path = guide ? `/guides/${guide.slug}` : '/404';

  usePageMeta({
    title: guide ? `${guide.title} – CloudNext` : 'Page not found – CloudNext',
    description: guide ? guide.description : 'Page not found.',
    path,
    jsonLd: guide
      ? [
        organizationSchema(),
        {
          '@type': 'Article',
          headline: guide.title,
          description: guide.description,
          author: { '@type': 'Person', name: 'Orfeo Hizon' },
          publisher: { '@id': 'https://cloudnext.dev/#organization' },
          mainEntityOfPage: `https://cloudnext.dev${path}`,
        },
        faqSchema(guide.faqs),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Guides', path: '/guides' }, { name: guide.title, path }]),
      ]
      : [organizationSchema()],
  });

  if (!guide) return <Navigate to="/404" replace />;

  return (
    <>
      <PageHero
        eyebrow="Guide"
        title={guide.title}
        description={guide.description}
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Guides', to: '/guides' }, { name: guide.title }]}
      >
        <ButtonLink to="/demo" variant="secondary">Hear the demo</ButtonLink>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-[0.75fr_1.25fr] lg:items-start">
            <div className="lg:sticky lg:top-24">
              <Card>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">In this guide</p>
                <div className="mt-3 space-y-2 text-sm">
                  {guide.sections.map((section) => (
                    <a key={section.heading} href={`#${section.heading.replace(/\s+/g, '-').toLowerCase()}`} className="block text-dust hover:text-gold-light">{section.heading}</a>
                  ))}
                </div>
                <div className="mt-5 border-t border-white/[0.06] pt-4">
                  <ButtonLink to="/contact" size="sm">Ask a question</ButtonLink>
                </div>
              </Card>
            </div>
            <div>
              <AnswerBlock>{guide.answer}</AnswerBlock>
              <Prose className="mt-8">
                {guide.sections.map((section) => (
                  <section key={section.heading} id={section.heading.replace(/\s+/g, '-').toLowerCase()}>
                    <h2>{section.heading}</h2>
                    {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  </section>
                ))}
              </Prose>
              <div className="mt-10">
                <SectionHeading eyebrow="Questions" title="Quick answers." />
                <div className="mt-5"><FAQList items={guide.faqs} /></div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <CTASection title="Have a question this guide did not answer?" description="Tell us the business and the problem. We will give you a straight answer, even if the answer is that you do not need this yet." primaryTo="/contact" primaryLabel="Ask CloudNext" secondaryTo="/guides" secondaryLabel="Back to guides" />
    </>
  );
}
